import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { AthleteProfile, HighlightClip } from '../types';
import { 
  Eye, 
  ShieldCheck, 
  Sparkles, 
  Video, 
  Upload, 
  CheckCircle2, 
  Building2, 
  GraduationCap, 
  Globe, 
  Award, 
  Star, 
  FileText, 
  Send, 
  Zap, 
  Lock, 
  ExternalLink,
  ChevronRight,
  Flame,
  BadgeCheck,
  Phone,
  Mail,
  Download,
  Search,
  Filter,
  CheckCircle,
  CreditCard,
  ShieldAlert,
  Users,
  Check,
  UserCheck,
  Clock,
  X,
  Key
} from 'lucide-react';

interface ScoutsViewProps {
  user: AthleteProfile;
  onUpdateProfile: (updated: Partial<AthleteProfile>) => void;
  showToast: (msg: string) => void;
  onOpenProCheckout?: () => void;
}

export interface ScoutSubmission {
  id: string;
  athleteId: string;
  athleteName: string;
  videoUrl: string;
  videoTitle: string;
  targetCategory: 'NCAA D1/D2' | 'Overseas FIBA Pro' | 'NBA G-League' | 'Prep Academy';
  submittedAt: string;
  status: 'Pending Verification' | 'Scout Reviewed' | 'Approved for Recruiting';
  scoutFeedback?: string;
  scoutName?: string;
  scoutOrg?: string;
}

export const ScoutsView: React.FC<ScoutsViewProps> = ({
  user,
  onUpdateProfile,
  showToast,
  onOpenProCheckout,
}) => {
  const [portalMode, setPortalMode] = useState<'athlete' | 'recruiter'>(
    user.isScout || user.role === 'scout_recruiter' ? 'recruiter' : 'athlete'
  );
  const [isVerifiedPro, setIsVerifiedPro] = useState<boolean>(user.isVerifiedPro || false);
  const [isVerifiedScout, setIsVerifiedScout] = useState<boolean>(
    user.isVerifiedScout || user.scoutPassActive || false
  );
  const [isSubmittingReel, setIsSubmittingReel] = useState<boolean>(false);
  const [selectedHighlight, setSelectedHighlight] = useState<string>('');
  const [customVideoUrl, setCustomVideoUrl] = useState<string>('');
  const [videoTitle, setVideoTitle] = useState<string>('');
  const [targetCategory, setTargetCategory] = useState<'NCAA D1/D2' | 'Overseas FIBA Pro' | 'NBA G-League' | 'Prep Academy'>('NCAA D1/D2');
  const [showUpgradeModal, setShowUpgradeModal] = useState<boolean>(false);
  const [showRecruiterPassModal, setShowRecruiterPassModal] = useState<boolean>(false);
  const [recruiterPassBillingCycle, setRecruiterPassBillingCycle] = useState<'monthly' | 'annual'>('monthly');

  // Scout Talent Search Filters
  const [prospectSearchQuery, setProspectSearchQuery] = useState('');
  const [selectedSportFilter, setSelectedSportFilter] = useState('all');
  const [selectedPositionFilter, setSelectedPositionFilter] = useState('all');

  // Submissions list
  const [submissions, setSubmissions] = useState<ScoutSubmission[]>([
    {
      id: 'scout_sub_1',
      athleteId: user?.id || 'guest',
      athleteName: user?.name || 'Athlete',
      videoUrl: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&w=1200&q=80',
      videoTitle: '2026 Summer Pickup League Season Highlights & Clutch Shots',
      targetCategory: 'NCAA D1/D2',
      submittedAt: '2026-08-01',
      status: 'Scout Reviewed',
      scoutName: 'Coach Marcus Vance',
      scoutOrg: 'Pacific Coast Scouting Director (NCAA Scout)',
      scoutFeedback: 'Explosive lateral movement and solid perimeter stroke. Strong court vision on drive-and-kicks. Recommended for Division 1 evaluation.',
    },
    {
      id: 'scout_sub_2',
      athleteId: user?.id || 'guest',
      athleteName: user?.name || 'Athlete',
      videoUrl: 'https://images.unsplash.com/photo-1519766304817-4f37bda74a29?auto=format&fit=crop&w=1200&q=80',
      videoTitle: '3v3 Tournament Championship Game Tape',
      targetCategory: 'Overseas FIBA Pro',
      submittedAt: '2026-08-03',
      status: 'Approved for Recruiting',
      scoutName: 'Elena Rostova',
      scoutOrg: 'EuroLeague Talent Evaluator',
      scoutFeedback: 'High basketball IQ, physical defensive motor. Verified stats confirmed by League Moderators.',
    },
  ]);

  // Mock Prospect Database for Scout Intelligence View
  const prospectsList = [
    {
      id: 'p1',
      name: 'Jordan "Flash" Miller',
      handle: '@jmiller',
      role: 'high_school',
      school: 'Oak Hill Prep Academy',
      sport: 'basketball',
      position: 'Point Guard',
      height: '6\'3"',
      weight: '185 lbs',
      ppg: 24.8,
      rpg: 5.2,
      apg: 8.4,
      phone: '(415) 892-0192',
      email: 'jordan.miller.d1recruit@gmail.com',
      videoUrl: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&w=800&q=80',
      verified: true,
      badge: '5-Star Prospect',
    },
    {
      id: 'p2',
      name: 'Marcus Vance',
      handle: '@mvance',
      role: 'college',
      school: 'UCLA Bruins',
      sport: 'basketball',
      position: 'Shooting Guard',
      height: '6\'6"',
      weight: '205 lbs',
      ppg: 21.4,
      rpg: 6.8,
      apg: 4.1,
      phone: '(310) 554-9120',
      email: 'm.vance.hoops@ucla.edu',
      videoUrl: 'https://images.unsplash.com/photo-1519766304817-4f37bda74a29?auto=format&fit=crop&w=800&q=80',
      verified: true,
      badge: 'NCAA All-American',
    },
    {
      id: 'p3',
      name: 'Elena Rostova',
      handle: '@erostova',
      role: 'playground_pro',
      school: 'FIBA EuroLeague Pool',
      sport: 'basketball',
      position: 'Small Forward',
      height: '6\'2"',
      weight: '175 lbs',
      ppg: 19.6,
      rpg: 8.9,
      apg: 5.3,
      phone: '+33 6 12 34 56 78',
      email: 'elena.rostova.pro@fiba.org',
      videoUrl: 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&w=800&q=80',
      verified: true,
      badge: 'Overseas Pro Verified',
    },
    {
      id: 'p4',
      name: 'Sarah Jenkins',
      handle: '@sjenkins',
      role: 'high_school',
      school: 'Sierra Canyon High',
      sport: 'basketball',
      position: 'Power Forward',
      height: '6\'1"',
      weight: '170 lbs',
      ppg: 22.1,
      rpg: 11.4,
      apg: 3.2,
      phone: '(213) 442-8819',
      email: 'sarah.j.prospects@yahoo.com',
      videoUrl: 'https://images.unsplash.com/photo-1518063319789-7217e6706b04?auto=format&fit=crop&w=800&q=80',
      verified: true,
      badge: 'Gatorade Player of Year',
    },
  ];

  const ppg = (user.stats?.basketball?.pts / (user.stats?.basketball?.gamesPlayed || 1)).toFixed(1);
  const rpg = (user.stats?.basketball?.reb / (user.stats?.basketball?.gamesPlayed || 1)).toFixed(1);
  const apg = (user.stats?.basketball?.ast / (user.stats?.basketball?.gamesPlayed || 1)).toFixed(1);

  const handleSubscribeVerifiedPro = () => {
    setShowUpgradeModal(false);
    if (onOpenProCheckout) {
      onOpenProCheckout();
    } else {
      setIsVerifiedPro(true);
      onUpdateProfile({ isVerifiedPro: true });
      showToast('🎉 Welcome to Verified Pro ($20/mo)! Official Moderator Blue Checkmark & Scout Priority unlocked!');
    }
  };

  const handleActivateRecruiterPass = () => {
    setShowRecruiterPassModal(false);
    setIsVerifiedScout(true);
    onUpdateProfile({
      isScout: true,
      isVerifiedScout: true,
      scoutPassActive: true,
      role: 'scout_recruiter',
      scoutOrgName: user.scoutOrgName || 'NCAA / FIBA Certified Scouting Network',
    });
    showToast('👁️ Recruiter Pass ($49/mo) Activated! Direct prospect contact sheets & raw tape downloads unlocked!');
  };

  const handleSubmitToScouts = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isVerifiedPro) {
      setShowUpgradeModal(true);
      return;
    }

    const finalUrl = customVideoUrl || selectedHighlight || (user?.highlights && user.highlights[0]?.videoUrl) || 'https://images.unsplash.com/photo-1546519638-68e109498ffc';
    const finalTitle = videoTitle || 'Athlete Game Tape Submission';

    const newSubmission: ScoutSubmission = {
      id: `scout_sub_${Date.now()}`,
      athleteId: user?.id || 'guest',
      athleteName: user?.name || 'Athlete',
      videoUrl: finalUrl,
      videoTitle: finalTitle,
      targetCategory,
      submittedAt: new Date().toISOString().split('T')[0],
      status: 'Pending Verification',
      scoutFeedback: 'Submitted to moderator queue for stat verification & scout routing.',
    };

    setSubmissions([newSubmission, ...submissions]);
    setIsSubmittingReel(false);
    setCustomVideoUrl('');
    setVideoTitle('');
    showToast(`🚀 Video reel "${finalTitle}" submitted to certified scouts for ${targetCategory}!`);
  };

  const [isScoutAccessGranted, setIsScoutAccessGranted] = useState<boolean>(
    !!(user.isScoutValidated || user.role === 'admin')
  );
  const [validationRequested, setValidationRequested] = useState<boolean>(
    user.scoutValidationStatus === 'pending'
  );
  const [showAdminPasscodeModal, setShowAdminPasscodeModal] = useState<boolean>(false);
  const [adminPasscode, setAdminPasscode] = useState<string>('');
  const [passcodeError, setPasscodeError] = useState<string>('');
  const [showPaymentProofModal, setShowPaymentProofModal] = useState<boolean>(false);
  const [paymentTxRef, setPaymentTxRef] = useState<string>('');

  const handleRequestValidation = () => {
    setValidationRequested(true);
    onUpdateProfile({ scoutValidationStatus: 'pending' });
    showToast('📩 Validation request submitted to Admin! The admin will grant Scout access once payment is verified.');
  };

  const handleVerifyPasscode = (e: React.FormEvent) => {
    e.preventDefault();
    if (adminPasscode.trim().toUpperCase() === 'ADMIN2026' || adminPasscode.trim().toLowerCase() === 'admin') {
      setIsScoutAccessGranted(true);
      setShowAdminPasscodeModal(false);
      onUpdateProfile({ isScoutValidated: true, scoutValidationStatus: 'approved' });
      showToast('⚡ Admin Passcode accepted! Scout Access Granted.');
    } else {
      setPasscodeError('Invalid Admin Passcode. Please contact platform admin.');
    }
  };

  const handleSubmitPaymentProof = (e: React.FormEvent) => {
    e.preventDefault();
    if (!paymentTxRef.trim()) return;
    setValidationRequested(true);
    setShowPaymentProofModal(false);
    onUpdateProfile({ scoutValidationStatus: 'pending' });
    showToast(`💳 Payment proof "${paymentTxRef}" submitted! Admin notified for account activation.`);
    setPaymentTxRef('');
  };

  if (!isScoutAccessGranted) {
    return (
      <div className="space-y-8 pb-12 max-w-5xl mx-auto">
        {/* Closed Off Header Banner */}
        <div className="relative overflow-hidden bg-gradient-to-r from-slate-950 via-indigo-950 to-slate-900 p-8 sm:p-12 rounded-[2.5rem] border border-amber-400/30 shadow-2xl text-white text-center">
          <div className="absolute top-0 right-0 w-96 h-96 bg-amber-400/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 space-y-4 max-w-3xl mx-auto">
            <div className="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full bg-amber-400/20 text-amber-300 border border-amber-400/40 text-xs font-black uppercase tracking-wider">
              <Lock className="w-4 h-4 text-amber-400" />
              <span>SCOUT NETWORK RESTRICTED • VALIDATED ACCOUNTS ONLY</span>
            </div>

            <h1 className="text-3xl sm:text-5xl font-black italic tracking-tight text-white uppercase">
              Exclusive Scout & Recruiter Portal
            </h1>

            <p className="text-sm sm:text-base text-indigo-200/80 leading-relaxed font-medium">
              The Scout Network is currently closed off and exclusive. Only available for validated accounts—access is provided solely by the platform administrator once payment & verification are confirmed.
            </p>
          </div>
        </div>

        {/* Current Account Status & Action Card */}
        <div className="bg-indigo-950/90 border border-white/10 rounded-[2rem] p-6 sm:p-8 space-y-6 shadow-xl relative overflow-hidden">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-white/10 pb-6">
            <div>
              <div className="text-xs uppercase font-black tracking-wider text-indigo-300/60 mb-1">
                Account Access Status
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-2xl bg-indigo-900 border border-white/10 flex items-center justify-center shrink-0">
                  <UserCheck className="w-5 h-5 text-indigo-300" />
                </div>
                <div>
                  <div className="font-extrabold text-white text-lg">{user.name} ({user.handle})</div>
                  <div className="text-xs text-indigo-300/70 font-mono">{user.email}</div>
                </div>
              </div>
            </div>

            {validationRequested ? (
              <span className="px-4 py-2 rounded-2xl bg-amber-400/20 text-amber-300 border border-amber-400/40 font-black italic text-xs uppercase flex items-center space-x-2">
                <Clock className="w-4 h-4 text-amber-400 animate-pulse" />
                <span>Validation Pending Admin Review</span>
              </span>
            ) : (
              <span className="px-4 py-2 rounded-2xl bg-rose-500/20 text-rose-300 border border-rose-500/30 font-black italic text-xs uppercase flex items-center space-x-2">
                <Lock className="w-4 h-4 text-rose-400" />
                <span>Unvalidated Account (Restricted)</span>
              </span>
            )}
          </div>

          {/* Action Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Request Validation */}
            <div className="bg-indigo-900/40 p-5 rounded-2xl border border-white/10 flex flex-col justify-between space-y-4 hover:border-lime-400/30 transition">
              <div className="space-y-2">
                <div className="w-10 h-10 rounded-xl bg-lime-400/20 border border-lime-400/30 flex items-center justify-center">
                  <Send className="w-5 h-5 text-lime-400" />
                </div>
                <h3 className="font-black italic text-white uppercase text-sm">Request Validation</h3>
                <p className="text-xs text-indigo-200/70">
                  Notify the platform administrator that you are awaiting account validation and Scout clearance.
                </p>
              </div>

              <button
                type="button"
                onClick={handleRequestValidation}
                disabled={validationRequested}
                className={`w-full py-3 rounded-xl font-black italic uppercase text-xs transition flex items-center justify-center space-x-2 ${
                  validationRequested
                    ? 'bg-indigo-900 text-indigo-300/50 cursor-not-allowed border border-white/5'
                    : 'bg-lime-400 hover:bg-lime-300 text-black shadow-lg shadow-lime-400/20'
                }`}
              >
                <span>{validationRequested ? 'Request Submitted' : 'Submit Validation Request'}</span>
              </button>
            </div>

            {/* Payment Proof */}
            <div className="bg-indigo-900/40 p-5 rounded-2xl border border-white/10 flex flex-col justify-between space-y-4 hover:border-purple-400/30 transition">
              <div className="space-y-2">
                <div className="w-10 h-10 rounded-xl bg-purple-500/20 border border-purple-400/30 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-purple-300" />
                </div>
                <h3 className="font-black italic text-white uppercase text-sm">Submit Payment Details</h3>
                <p className="text-xs text-indigo-200/70">
                  Already paid? Send your transaction ID or receipt reference directly to the admin for fast activation.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowPaymentProofModal(true)}
                className="w-full py-3 bg-purple-600 hover:bg-purple-500 text-white font-black italic uppercase text-xs rounded-xl shadow-lg shadow-purple-600/20 transition flex items-center justify-center space-x-2"
              >
                <span>Submit Payment Proof</span>
              </button>
            </div>

            {/* Admin Passcode Override */}
            <div className="bg-indigo-900/40 p-5 rounded-2xl border border-white/10 flex flex-col justify-between space-y-4 hover:border-amber-400/30 transition">
              <div className="space-y-2">
                <div className="w-10 h-10 rounded-xl bg-amber-400/20 border border-amber-400/30 flex items-center justify-center">
                  <Key className="w-5 h-5 text-amber-300" />
                </div>
                <h3 className="font-black italic text-white uppercase text-sm">Admin Access Override</h3>
                <p className="text-xs text-indigo-200/70">
                  Have an admin passcode or testing key? Unlock full Scout portal access instantly.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowAdminPasscodeModal(true)}
                className="w-full py-3 bg-indigo-900 hover:bg-indigo-800 text-amber-300 border border-amber-400/30 font-black italic uppercase text-xs rounded-xl transition flex items-center justify-center space-x-2"
              >
                <span>Enter Admin Key</span>
              </button>
            </div>
          </div>
        </div>

        {/* Locked Feature Preview Cards */}
        <div className="space-y-4">
          <div className="flex items-center space-x-2 text-xs font-black uppercase italic text-indigo-300/80">
            <Lock className="w-4 h-4 text-amber-400" />
            <span>Gated Scout Portal Features (Validated Accounts Only)</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 opacity-75">
            <div className="bg-indigo-950/50 p-6 rounded-2xl border border-white/10 space-y-3 relative overflow-hidden backdrop-blur-sm">
              <div className="absolute top-3 right-3 px-2 py-1 bg-amber-400/20 text-amber-300 rounded-lg text-[10px] font-black uppercase border border-amber-400/30">
                Gated
              </div>
              <GraduationCap className="w-8 h-8 text-indigo-400" />
              <h4 className="font-extrabold text-white text-base">NCAA D1/D2 Reel Routing</h4>
              <p className="text-xs text-indigo-200/60 leading-relaxed">
                Direct submission queue to certified college recruiters, FIBA talent evaluators, and NBA G-League scout boards.
              </p>
            </div>

            <div className="bg-indigo-950/50 p-6 rounded-2xl border border-white/10 space-y-3 relative overflow-hidden backdrop-blur-sm">
              <div className="absolute top-3 right-3 px-2 py-1 bg-amber-400/20 text-amber-300 rounded-lg text-[10px] font-black uppercase border border-amber-400/30">
                Gated
              </div>
              <ShieldCheck className="w-8 h-8 text-lime-400" />
              <h4 className="font-extrabold text-white text-base">Verified Performance Audit</h4>
              <p className="text-xs text-indigo-200/60 leading-relaxed">
                Official moderator validation stamp appended to match stats, shooting percentage logs, and athletic drill results.
              </p>
            </div>

            <div className="bg-indigo-950/50 p-6 rounded-2xl border border-white/10 space-y-3 relative overflow-hidden backdrop-blur-sm">
              <div className="absolute top-3 right-3 px-2 py-1 bg-amber-400/20 text-amber-300 rounded-lg text-[10px] font-black uppercase border border-amber-400/30">
                Gated
              </div>
              <FileText className="w-8 h-8 text-purple-400" />
              <h4 className="font-extrabold text-white text-base">Recruiter Contact Sheets</h4>
              <p className="text-xs text-indigo-200/60 leading-relaxed">
                Unredacted prospect contact info, phone numbers, raw video downloads, and digital offer letter dispatches.
              </p>
            </div>
          </div>
        </div>

        {/* Modals for Payment Proof & Admin Passcode */}
        {showPaymentProofModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
            <div className="bg-indigo-950 border border-white/20 rounded-3xl max-w-md w-full p-6 space-y-6 text-white shadow-2xl relative">
              <div className="flex items-center justify-between border-b border-white/10 pb-4">
                <div className="flex items-center space-x-2">
                  <CreditCard className="w-5 h-5 text-purple-400" />
                  <h3 className="font-black italic text-lg uppercase">Submit Payment Proof</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPaymentProofModal(false)}
                  className="p-1 rounded-lg text-indigo-300 hover:text-white hover:bg-white/10"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmitPaymentProof} className="space-y-4">
                <p className="text-xs text-indigo-200/80">
                  Enter your payment transaction reference, invoice number, or payment email. The admin will verify payment and unlock your Scout account.
                </p>

                <div>
                  <label className="block text-xs font-bold text-indigo-300 uppercase mb-1">
                    Transaction ID / Reference or Email
                  </label>
                  <input
                    type="text"
                    required
                    value={paymentTxRef}
                    onChange={(e) => setPaymentTxRef(e.target.value)}
                    placeholder="e.g. TXN-982301 or user@example.com"
                    className="w-full bg-indigo-900 border border-white/10 rounded-xl px-4 py-2.5 text-xs text-white placeholder-indigo-300/40 focus:outline-none focus:ring-2 focus:ring-purple-400"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowPaymentProofModal(false)}
                    className="flex-1 py-3 bg-indigo-900 hover:bg-indigo-800 text-xs font-bold rounded-xl text-indigo-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-3 bg-purple-600 hover:bg-purple-500 text-white font-black italic uppercase text-xs rounded-xl shadow-lg"
                  >
                    Submit Proof
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showAdminPasscodeModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
            <div className="bg-indigo-950 border border-amber-400/30 rounded-3xl max-w-md w-full p-6 space-y-6 text-white shadow-2xl relative">
              <div className="flex items-center justify-between border-b border-white/10 pb-4">
                <div className="flex items-center space-x-2">
                  <Key className="w-5 h-5 text-amber-300" />
                  <h3 className="font-black italic text-lg uppercase text-amber-300">Admin Passcode Override</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAdminPasscodeModal(false)}
                  className="p-1 rounded-lg text-indigo-300 hover:text-white hover:bg-white/10"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleVerifyPasscode} className="space-y-4">
                <p className="text-xs text-indigo-200/80">
                  Enter the Admin Key to validate this account and unlock full Scout Network capabilities. (Default Demo Key: <code className="text-amber-300 font-bold">ADMIN2026</code>)
                </p>

                <div>
                  <label className="block text-xs font-bold text-amber-300 uppercase mb-1">
                    Passcode / Access Key
                  </label>
                  <input
                    type="password"
                    required
                    value={adminPasscode}
                    onChange={(e) => {
                      setAdminPasscode(e.target.value);
                      setPasscodeError('');
                    }}
                    placeholder="Enter admin passcode..."
                    className="w-full bg-indigo-900 border border-white/10 rounded-xl px-4 py-2.5 text-xs text-white placeholder-indigo-300/40 focus:outline-none focus:ring-2 focus:ring-amber-400"
                  />
                  {passcodeError && (
                    <div className="text-[11px] text-rose-400 font-bold mt-1">
                      {passcodeError}
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setIsScoutAccessGranted(true);
                      setShowAdminPasscodeModal(false);
                      onUpdateProfile({ isScoutValidated: true, scoutValidationStatus: 'approved' });
                      showToast('⚡ Instant Admin Bypass Activated! Scout Access Granted.');
                    }}
                    className="flex-1 py-3 bg-amber-400/20 hover:bg-amber-400/30 text-amber-300 border border-amber-400/40 font-black italic uppercase text-[10px] rounded-xl"
                  >
                    Instant Admin Unlock
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-3 bg-amber-400 hover:bg-amber-300 text-black font-black italic uppercase text-xs rounded-xl shadow-lg"
                  >
                    Verify Key
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12 max-w-7xl mx-auto">
      {/* Scout Hub Header Banner */}
      <div className="relative overflow-hidden bg-gradient-to-r from-indigo-950 via-slate-900 to-indigo-900 p-6 sm:p-8 rounded-[2.5rem] border border-amber-400/30 shadow-2xl text-white">
        {/* Glow Effects */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-400/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-lime-400/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-2 max-w-2xl">
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 bg-amber-400/20 text-amber-300 border border-amber-400/40 text-xs font-black italic uppercase rounded-full flex items-center gap-1.5">
                <Eye className="w-3.5 h-3.5 text-amber-400" />
                <span>Pro Scout Network Hub</span>
              </span>
              {isVerifiedPro && (
                <span className="px-3 py-1 bg-lime-400/20 text-lime-300 border border-lime-400/40 text-xs font-extrabold rounded-full flex items-center gap-1">
                  <BadgeCheck className="w-3.5 h-3.5 text-lime-400 fill-lime-400/20" />
                  <span>Verified Pro ($20/mo) Active</span>
                </span>
              )}
            </div>

            <h1 className="text-3xl sm:text-4xl font-black italic tracking-tight text-white">
              Connect With Certified College & Overseas Scouts 🏀
            </h1>
            <p className="text-sm text-indigo-200/80 leading-relaxed">
              Submit your verified stats and game tapes directly to NCAA Division 1/2, EuroLeague, and G-League scouts hiring future professional talent.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0 w-full md:w-auto">
            {!isVerifiedPro ? (
              <button
                type="button"
                onClick={() => setShowUpgradeModal(true)}
                className="px-6 py-3 bg-gradient-to-r from-amber-400 to-lime-400 hover:from-amber-300 hover:to-lime-300 text-black font-black italic uppercase text-xs rounded-2xl shadow-xl transition-all duration-200 flex items-center justify-center gap-2 group"
              >
                <Sparkles className="w-4 h-4 text-black" />
                <span>Get Verified Pro ($20/mo)</span>
                <ChevronRight className="w-4 h-4 text-black group-hover:translate-x-1 transition-transform" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setIsSubmittingReel(true)}
                className="px-6 py-3 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-2xl shadow-xl transition flex items-center justify-center gap-2"
              >
                <Upload className="w-4 h-4 text-black" />
                <span>Submit Video Reel to Scouts</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Portal Mode Segmented Control */}
      <div className="flex bg-indigo-950/90 p-1.5 rounded-2xl border border-white/10 max-w-xl mx-auto shadow-xl">
        <button
          type="button"
          onClick={() => setPortalMode('athlete')}
          className={`flex-1 py-3 px-4 rounded-xl text-xs font-black italic uppercase transition flex items-center justify-center gap-2 ${
            portalMode === 'athlete'
              ? 'bg-lime-400 text-black shadow-lg shadow-lime-400/20'
              : 'text-indigo-300 hover:text-white'
          }`}
        >
          <Upload className="w-4 h-4" />
          <span>Athlete Exposure Portal</span>
        </button>

        <button
          type="button"
          onClick={() => setPortalMode('recruiter')}
          className={`flex-1 py-3 px-4 rounded-xl text-xs font-black italic uppercase transition flex items-center justify-center gap-2 ${
            portalMode === 'recruiter'
              ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/20'
              : 'text-indigo-300 hover:text-white'
          }`}
        >
          <Users className="w-4 h-4" />
          <span>Scout Intelligence Hub</span>
          {!isVerifiedScout && (
            <span className="px-1.5 py-0.5 bg-amber-400 text-black font-black text-[9px] rounded-md uppercase">
              $49/mo
            </span>
          )}
        </button>
      </div>

      {portalMode === 'recruiter' ? (
        /* RECRUITER INTELLIGENCE HUB VIEW */
        <div className="space-y-6 animate-fadeIn">
          {/* Recruiter Banner & Status */}
          <div className="bg-gradient-to-r from-purple-950 via-indigo-950 to-slate-900 p-6 rounded-[2.5rem] border border-purple-500/30 shadow-2xl space-y-4 text-white">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 bg-purple-500/20 text-purple-300 border border-purple-500/40 text-xs font-black italic uppercase rounded-full flex items-center gap-1.5">
                    <UserCheck className="w-3.5 h-3.5 text-purple-400" />
                    <span>NCAA & FIBA Talent Database</span>
                  </span>
                  {isVerifiedScout ? (
                    <span className="px-3 py-1 bg-emerald-400/20 text-emerald-300 border border-emerald-400/40 text-xs font-extrabold rounded-full flex items-center gap-1">
                      <BadgeCheck className="w-3.5 h-3.5 text-emerald-400 fill-emerald-400/20" />
                      <span>Recruiter Pass Active</span>
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-amber-400/20 text-amber-300 border border-amber-400/40 text-xs font-extrabold rounded-full flex items-center gap-1">
                      <Lock className="w-3.5 h-3.5 text-amber-400" />
                      <span>Free Scout Preview (Gated)</span>
                    </span>
                  )}
                </div>

                <h2 className="text-2xl sm:text-3xl font-black italic uppercase tracking-tight text-white">
                  College & Professional Talent Scouting
                </h2>
                <p className="text-xs text-indigo-200/80 max-w-2xl">
                  Search verified high school varsity, NCAA college transfer portal, and overseas FIBA prospects. Access game reels, stat logs, and unredacted contact sheets.
                </p>
              </div>

              {!isVerifiedScout && (
                <button
                  type="button"
                  onClick={() => setShowRecruiterPassModal(true)}
                  className="px-6 py-3.5 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-400 hover:to-indigo-400 text-white font-black italic uppercase text-xs rounded-2xl shadow-xl transition flex items-center justify-center gap-2 shrink-0 border border-purple-300/30"
                >
                  <Sparkles className="w-4 h-4 text-amber-300" />
                  <span>Activate Recruiter Pass ($49/mo)</span>
                </button>
              )}
            </div>

            {!isVerifiedScout && (
              <div className="p-3.5 bg-amber-500/15 border border-amber-500/30 rounded-2xl text-xs text-amber-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <ShieldAlert className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>
                    <strong>Free Scout Mode:</strong> Direct phone numbers, unredacted emails, raw 1080p game tape downloads, and offer letter dispatching are locked until <strong>Recruiter Pass ($49/mo)</strong> activation.
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setShowRecruiterPassModal(true)}
                  className="px-3 py-1 bg-amber-400 hover:bg-amber-300 text-black font-black italic text-[11px] uppercase rounded-xl shrink-0"
                >
                  Unlock Access
                </button>
              </div>
            )}
          </div>

          {/* Talent Search Filters */}
          <div className="bg-indigo-900/60 p-4 rounded-2xl border border-white/10 shadow-lg flex flex-col sm:flex-row items-center gap-3">
            <div className="relative flex-1 w-full">
              <Search className="w-4 h-4 text-indigo-300 absolute left-3 top-3" />
              <input
                type="text"
                placeholder="Search athlete by name, school, handle..."
                value={prospectSearchQuery}
                onChange={(e) => setProspectSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-indigo-950 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-purple-400"
              />
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto">
              <select
                value={selectedSportFilter}
                onChange={(e) => setSelectedSportFilter(e.target.value)}
                className="p-2 bg-indigo-950 border border-white/10 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-purple-400"
              >
                <option value="all">All Sports 🏀🏐⚾</option>
                <option value="volleyball">Volleyball 🏐</option>
                <option value="basketball">Basketball 🏀</option>
                <option value="baseball">Baseball ⚾</option>
                <option value="soccer">Soccer ⚽</option>
                <option value="football">Football 🏈</option>
                <option value="pickleball">Pickleball 🏓</option>
              </select>

              <select
                value={selectedPositionFilter}
                onChange={(e) => setSelectedPositionFilter(e.target.value)}
                className="p-2 bg-indigo-950 border border-white/10 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-purple-400"
              >
                <option value="all">All Positions</option>
                <option value="Point Guard">Point Guard</option>
                <option value="Shooting Guard">Shooting Guard</option>
                <option value="Small Forward">Small Forward</option>
                <option value="Power Forward">Power Forward</option>
              </select>
            </div>
          </div>

          {/* Prospects Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {prospectsList
              .filter((p) => {
                if (
                  prospectSearchQuery &&
                  !p.name.toLowerCase().includes(prospectSearchQuery.toLowerCase()) &&
                  !p.school.toLowerCase().includes(prospectSearchQuery.toLowerCase())
                ) {
                  return false;
                }
                if (selectedPositionFilter !== 'all' && p.position !== selectedPositionFilter) {
                  return false;
                }
                return true;
              })
              .map((prospect) => (
                <div
                  key={prospect.id}
                  className="bg-indigo-950/90 rounded-3xl border border-white/15 p-5 space-y-4 shadow-2xl relative overflow-hidden text-white"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-14 h-14 rounded-2xl overflow-hidden border-2 border-purple-400/60 shrink-0 bg-indigo-900">
                        <img
                          src={prospect.videoUrl}
                          alt={prospect.name}
                          className="w-full h-full object-cover"
                          referrerPolicy="no-referrer"
                        />
                      </div>

                      <div className="space-y-0.5">
                        <div className="flex items-center gap-1.5">
                          <h3 className="font-extrabold text-white text-base">{prospect.name}</h3>
                          <BadgeCheck className="w-4 h-4 text-purple-400 fill-purple-400/20" />
                        </div>
                        <p className="text-xs text-indigo-300 font-medium">
                          {prospect.position} • {prospect.school}
                        </p>
                        <span className="inline-block px-2 py-0.5 bg-purple-500/20 text-purple-300 text-[10px] font-black italic rounded-md">
                          {prospect.badge}
                        </span>
                      </div>
                    </div>

                    <div className="text-right space-y-0.5 font-mono">
                      <div className="text-sm font-black text-lime-400">{prospect.ppg} PPG</div>
                      <div className="text-[10px] text-indigo-300">{prospect.rpg} RPG • {prospect.apg} APG</div>
                    </div>
                  </div>

                  {/* Physical Specs & Verified Stats */}
                  <div className="grid grid-cols-3 gap-2 bg-indigo-900/60 p-3 rounded-2xl border border-white/5 text-center text-xs">
                    <div>
                      <span className="text-[10px] text-indigo-300 block">Height</span>
                      <strong className="text-white font-mono">{prospect.height}</strong>
                    </div>
                    <div>
                      <span className="text-[10px] text-indigo-300 block">Weight</span>
                      <strong className="text-white font-mono">{prospect.weight}</strong>
                    </div>
                    <div>
                      <span className="text-[10px] text-indigo-300 block">Scout Status</span>
                      <strong className="text-lime-300 font-mono">Verified D1</strong>
                    </div>
                  </div>

                  {/* Unredacted vs Gated Contact Info */}
                  <div className="p-3 bg-indigo-900/40 rounded-2xl border border-white/10 space-y-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-indigo-300/80 font-bold flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5 text-lime-400" />
                        <span>Direct Cell Phone:</span>
                      </span>
                      {isVerifiedScout ? (
                        <a
                          href={`tel:${prospect.phone}`}
                          className="font-mono text-lime-300 font-bold hover:underline"
                        >
                          {prospect.phone}
                        </a>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-indigo-300/40 blur-[3px] select-none">(415) 892-****</span>
                          <button
                            type="button"
                            onClick={() => setShowRecruiterPassModal(true)}
                            className="text-[10px] bg-purple-500/30 hover:bg-purple-500/50 text-purple-200 px-2 py-0.5 rounded-md font-bold flex items-center gap-1"
                          >
                            <Lock className="w-2.5 h-2.5" />
                            <span>Unlock</span>
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between border-t border-white/5 pt-1.5">
                      <span className="text-indigo-300/80 font-bold flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5 text-cyan-400" />
                        <span>Athlete Email Sheet:</span>
                      </span>
                      {isVerifiedScout ? (
                        <a
                          href={`mailto:${prospect.email}`}
                          className="font-mono text-cyan-300 font-bold hover:underline"
                        >
                          {prospect.email}
                        </a>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-indigo-300/40 blur-[3px] select-none">prospect***@gmail.com</span>
                          <button
                            type="button"
                            onClick={() => setShowRecruiterPassModal(true)}
                            className="text-[10px] bg-purple-500/30 hover:bg-purple-500/50 text-purple-200 px-2 py-0.5 rounded-md font-bold flex items-center gap-1"
                          >
                            <Lock className="w-2.5 h-2.5" />
                            <span>Unlock</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Scout Action Trigger Buttons */}
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => {
                        if (!isVerifiedScout) {
                          setShowRecruiterPassModal(true);
                        } else {
                          showToast(`📥 Downloading 1080p game tape file for ${prospect.name}...`);
                        }
                      }}
                      className="py-2.5 px-3 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 font-bold text-xs rounded-xl flex items-center justify-center gap-1.5 border border-white/10 transition"
                    >
                      <Download className="w-3.5 h-3.5 text-lime-400" />
                      <span>Download Raw Tape</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        if (!isVerifiedScout) {
                          setShowRecruiterPassModal(true);
                        } else {
                          showToast(`✉️ Official Recruitment Offer Letter dispatched to ${prospect.name}!`);
                        }
                      }}
                      className="py-2.5 px-3 bg-purple-600 hover:bg-purple-500 text-white font-extrabold text-xs rounded-xl flex items-center justify-center gap-1.5 shadow-md transition"
                    >
                      <Send className="w-3.5 h-3.5" />
                      <span>Issue Offer Letter</span>
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      ) : (
        /* ATHLETE EXPOSURE PORTAL VIEW */
        <div className="space-y-6">

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {user.primarySport === 'volleyball' ? (
          <>
            <div className="bg-indigo-950/80 p-4 rounded-2xl border border-white/10 flex flex-col justify-between space-y-1">
              <span className="text-[10px] text-indigo-300/70 font-extrabold uppercase">Volleyball Kills 💥</span>
              <span className="text-xl font-black font-mono text-lime-400">{user.stats?.volleyball?.kills || 0} Kills</span>
              <span className="text-[9px] text-indigo-300/60 font-semibold">Spike Finishes Logged</span>
            </div>
            <div className="bg-indigo-950/80 p-4 rounded-2xl border border-white/10 flex flex-col justify-between space-y-1">
              <span className="text-[10px] text-indigo-300/70 font-extrabold uppercase">Net Blocks 🛡️</span>
              <span className="text-xl font-black font-mono text-cyan-300">{user.stats?.volleyball?.blocks || 0} Blocks</span>
              <span className="text-[9px] text-indigo-300/60 font-semibold">Tape Rejections Logged</span>
            </div>
            <div className="bg-indigo-950/80 p-4 rounded-2xl border border-white/10 flex flex-col justify-between space-y-1">
              <span className="text-[10px] text-indigo-300/70 font-extrabold uppercase">Service Aces ⚡</span>
              <span className="text-xl font-black font-mono text-amber-300">{user.stats?.volleyball?.aces || 0} Aces</span>
              <span className="text-[9px] text-indigo-300/60 font-semibold">Clean Unreturned Serves</span>
            </div>
          </>
        ) : (
          <>
            <div className="bg-indigo-950/80 p-4 rounded-2xl border border-white/10 flex flex-col justify-between space-y-1">
              <span className="text-[10px] text-indigo-300/70 font-extrabold uppercase">Verified Scoring Avg</span>
              <span className="text-xl font-black font-mono text-lime-400">{ppg} PPG</span>
              <span className="text-[9px] text-indigo-300/60 font-semibold">{user.stats?.basketball?.pts || 0} Total Career PTS</span>
            </div>
            <div className="bg-indigo-950/80 p-4 rounded-2xl border border-white/10 flex flex-col justify-between space-y-1">
              <span className="text-[10px] text-indigo-300/70 font-extrabold uppercase">Rebound Avg</span>
              <span className="text-xl font-black font-mono text-cyan-300">{rpg} RPG</span>
              <span className="text-[9px] text-indigo-300/60 font-semibold">{user.stats?.basketball?.reb || 0} Boards Logged</span>
            </div>
            <div className="bg-indigo-950/80 p-4 rounded-2xl border border-white/10 flex flex-col justify-between space-y-1">
              <span className="text-[10px] text-indigo-300/70 font-extrabold uppercase">Assists Avg</span>
              <span className="text-xl font-black font-mono text-amber-300">{apg} APG</span>
              <span className="text-[9px] text-indigo-300/60 font-semibold">{user.stats?.basketball?.ast || 0} Dimes Passed</span>
            </div>
          </>
        )}
        <div className="bg-indigo-950/80 p-4 rounded-2xl border border-white/10 flex flex-col justify-between space-y-1">
          <span className="text-[10px] text-indigo-300/70 font-extrabold uppercase">Moderator Badge</span>
          <div className="flex items-center gap-1.5 pt-1">
            {isVerifiedPro ? (
              <>
                <BadgeCheck className="w-5 h-5 text-lime-400 fill-lime-400/20" />
                <span className="text-xs font-bold text-lime-300">Stats Verified ✓</span>
              </>
            ) : (
              <>
                <ShieldCheck className="w-5 h-5 text-indigo-400" />
                <span className="text-xs font-bold text-indigo-300">Unverified Account</span>
              </>
            )}
          </div>
          <span className="text-[9px] text-indigo-300/60 font-semibold">{isVerifiedPro ? 'Moderator Audit Complete' : '$20/mo upgrade for badge'}</span>
        </div>
      </div>

      {/* Scout Organizations Directory */}
      <div className="bg-indigo-900/60 p-6 rounded-3xl border border-white/10 shadow-xl space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-black italic uppercase text-white flex items-center gap-2">
            <Building2 className="w-5 h-5 text-amber-400" />
            <span>Active Scout Directory & Recruiting Partners</span>
          </h2>
          <span className="text-xs font-extrabold text-lime-400 bg-lime-400/10 px-3 py-1 rounded-full border border-lime-400/20">
            4 Scouts Online Now
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-indigo-950/80 p-4 rounded-2xl border border-white/10 space-y-2">
            <div className="p-2 w-10 h-10 rounded-xl bg-amber-400/20 text-amber-300 border border-amber-400/30 flex items-center justify-center">
              <GraduationCap className="w-5 h-5" />
            </div>
            <h3 className="font-bold text-white text-sm">NCAA D1 / D2 Scouts</h3>
            <p className="text-xs text-indigo-200/70">Evaluating athletic prospects for collegiate scholarships.</p>
            <span className="text-[10px] text-lime-400 font-mono block">Recruiting Target: 18-24 yrs</span>
          </div>

          <div className="bg-indigo-950/80 p-4 rounded-2xl border border-white/10 space-y-2">
            <div className="p-2 w-10 h-10 rounded-xl bg-cyan-400/20 text-cyan-300 border border-cyan-400/30 flex items-center justify-center">
              <Globe className="w-5 h-5" />
            </div>
            <h3 className="font-bold text-white text-sm">FIBA Overseas Pro</h3>
            <p className="text-xs text-indigo-200/70">EuroLeague, LNB Pro A, & Asia League talent scouts.</p>
            <span className="text-[10px] text-cyan-300 font-mono block">Professional Salaries</span>
          </div>

          <div className="bg-indigo-950/80 p-4 rounded-2xl border border-white/10 space-y-2">
            <div className="p-2 w-10 h-10 rounded-xl bg-purple-400/20 text-purple-300 border border-purple-400/30 flex items-center justify-center">
              <Award className="w-5 h-5" />
            </div>
            <h3 className="font-bold text-white text-sm">NBA G-League</h3>
            <p className="text-xs text-indigo-200/70">Development league scouts looking for explosive guards & wings.</p>
            <span className="text-[10px] text-purple-300 font-mono block">Tryout Invites</span>
          </div>

          <div className="bg-indigo-950/80 p-4 rounded-2xl border border-white/10 space-y-2">
            <div className="p-2 w-10 h-10 rounded-xl bg-rose-400/20 text-rose-300 border border-rose-400/30 flex items-center justify-center">
              <Star className="w-5 h-5" />
            </div>
            <h3 className="font-bold text-white text-sm">Prep & Academy Scouts</h3>
            <p className="text-xs text-indigo-200/70">High school elite prep circuits and exposure camps.</p>
            <span className="text-[10px] text-rose-300 font-mono block">National Ranking</span>
          </div>
        </div>
      </div>

      {/* Scout Submissions & Evaluation Feed */}
      <div className="bg-indigo-900/60 p-6 rounded-3xl border border-white/10 shadow-xl space-y-5">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-black italic uppercase text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-lime-400" />
              <span>Your Scout Submissions & Evaluations</span>
            </h2>
            <p className="text-xs text-indigo-200/70">
              Track video reel submissions and official scout feedback cards.
            </p>
          </div>

          <button
            type="button"
            onClick={() => {
              if (!isVerifiedPro) {
                setShowUpgradeModal(true);
              } else {
                setIsSubmittingReel(true);
              }
            }}
            className="px-4 py-2 bg-indigo-800 hover:bg-indigo-700 text-lime-300 border border-lime-400/30 rounded-xl text-xs font-bold transition flex items-center gap-1.5"
          >
            <Upload className="w-3.5 h-3.5 text-lime-400" />
            <span>New Submission</span>
          </button>
        </div>

        <div className="space-y-4">
          {submissions.map((sub) => (
            <div
              key={sub.id}
              className="bg-indigo-950/80 p-5 rounded-2xl border border-white/10 space-y-4 relative overflow-hidden"
            >
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex items-center space-x-4">
                  <div className="w-20 h-14 rounded-xl overflow-hidden bg-indigo-900 relative shrink-0">
                    <img
                      src={sub.videoUrl}
                      alt={sub.videoTitle}
                      className="w-full h-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                      <Video className="w-5 h-5 text-white/90" />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-amber-400/20 text-amber-300 text-[10px] font-extrabold rounded-md uppercase">
                        {sub.targetCategory}
                      </span>
                      <span className="text-[10px] text-indigo-300/60 font-mono">
                        Submitted on {sub.submittedAt}
                      </span>
                    </div>
                    <h3 className="font-extrabold text-white text-sm">{sub.videoTitle}</h3>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span
                    className={`px-3 py-1 text-xs font-extrabold rounded-full border ${
                      sub.status === 'Approved for Recruiting'
                        ? 'bg-emerald-400/20 text-emerald-300 border-emerald-400/40'
                        : sub.status === 'Scout Reviewed'
                        ? 'bg-lime-400/20 text-lime-300 border-lime-400/40'
                        : 'bg-amber-400/20 text-amber-300 border-amber-400/40'
                    }`}
                  >
                    {sub.status}
                  </span>
                </div>
              </div>

              {/* Scout Feedback Card */}
              {sub.scoutFeedback && (
                <div className="p-3.5 bg-indigo-900/60 rounded-xl border border-white/5 text-xs space-y-1.5">
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="font-extrabold text-lime-400 flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5 text-lime-400" />
                      <span>Evaluation by {sub.scoutName || 'Certified Scout'}</span>
                    </span>
                    <span className="text-indigo-300/70 font-semibold">{sub.scoutOrg || 'Recruiting Partner'}</span>
                  </div>
                  <p className="text-indigo-100 italic leading-relaxed">
                    "{sub.scoutFeedback}"
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Submission Modal */}
      {isSubmittingReel && (
        <div className="fixed inset-0 z-50 bg-indigo-950/85 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-indigo-900 border border-white/15 rounded-[2.5rem] p-6 max-w-lg w-full text-white space-y-5 relative shadow-2xl">
            <div className="flex justify-between items-center border-b border-white/10 pb-3">
              <h3 className="text-lg font-black italic uppercase text-white flex items-center gap-2">
                <Upload className="w-5 h-5 text-lime-400" />
                <span>Submit Video Reel to Scouts</span>
              </h3>
              <button
                onClick={() => setIsSubmittingReel(false)}
                className="text-indigo-300 hover:text-white font-bold text-lg"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmitToScouts} className="space-y-4">
              <div>
                <label className="text-xs font-bold text-indigo-200 block mb-1">
                  Highlight Reel Title
                </label>
                <input
                  type="text"
                  placeholder="e.g. Summer 2026 Season Game Tape & Clutch Highlights"
                  value={videoTitle}
                  onChange={(e) => setVideoTitle(e.target.value)}
                  className="w-full p-3 bg-indigo-950 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
                  required
                />
              </div>

              <div>
                <label className="text-xs font-bold text-indigo-200 block mb-1">
                  Target League Category
                </label>
                <select
                  value={targetCategory}
                  onChange={(e) => setTargetCategory(e.target.value as any)}
                  className="w-full p-3 bg-indigo-950 border border-white/10 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-lime-400"
                >
                  <option value="NCAA D1/D2">NCAA D1 / D2 College Recruiting</option>
                  <option value="Overseas FIBA Pro">Overseas FIBA Pro Leagues</option>
                  <option value="NBA G-League">NBA G-League Tryouts</option>
                  <option value="Prep Academy">Prep Academy / High School Circuit</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-bold text-indigo-200 block mb-1">
                  Video URL (YouTube / Vimeo / Direct Link)
                </label>
                <input
                  type="url"
                  placeholder="https://youtube.com/watch?v=..."
                  value={customVideoUrl}
                  onChange={(e) => setCustomVideoUrl(e.target.value)}
                  className="w-full p-3 bg-indigo-950 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
                />
              </div>

              {user.highlights && user.highlights.length > 0 && (
                <div>
                  <label className="text-xs font-bold text-indigo-200 block mb-1">
                    Or Select Existing App Reel
                  </label>
                  <select
                    value={selectedHighlight}
                    onChange={(e) => setSelectedHighlight(e.target.value)}
                    className="w-full p-3 bg-indigo-950 border border-white/10 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-lime-400"
                  >
                    <option value="">-- Choose from existing uploaded reels --</option>
                    {user.highlights.map((h) => (
                      <option key={h.id} value={h.videoUrl}>
                        {h.title} ({h.sport})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="bg-indigo-950 p-3.5 rounded-xl border border-white/10 text-xs space-y-1">
                <span className="font-extrabold text-lime-400 block">Verified Stats Package Included:</span>
                <p className="text-indigo-200/80 text-[11px]">
                  Your profile stats ({ppg} PPG, {rpg} RPG, {apg} APG) will be attached alongside moderator audit logs.
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsSubmittingReel(false)}
                  className="px-4 py-2.5 bg-indigo-950 text-indigo-300 font-bold text-xs rounded-xl"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-xl shadow-lg transition"
                >
                  Submit to Scouts
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
        </div>
      )}

      {/* Verified Pro $20/mo Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-50 bg-indigo-950/85 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-gradient-to-b from-indigo-900 to-indigo-950 border border-amber-400/40 rounded-[2.5rem] p-6 sm:p-8 max-w-lg w-full text-white space-y-6 relative shadow-2xl">
            <button
              onClick={() => setShowUpgradeModal(false)}
              className="absolute top-5 right-5 text-indigo-300 hover:text-white font-bold text-lg"
            >
              ✕
            </button>

            <div className="text-center space-y-2">
              <div className="inline-flex items-center justify-center p-3 rounded-2xl bg-amber-400/20 text-amber-300 border border-amber-400/40">
                <BadgeCheck className="w-8 h-8 text-amber-400 fill-amber-400/20" />
              </div>
              <h2 className="text-2xl font-black italic uppercase tracking-tight text-white">
                Unlock Verified Pro ($20 / Month)
              </h2>
              <p className="text-xs text-indigo-200/80 max-w-sm mx-auto">
                Get your stats and game tapes officially audited by platform moderators and reviewed by certified college & overseas scouts.
              </p>
            </div>

            <div className="space-y-3 bg-indigo-950/80 p-4 rounded-2xl border border-white/10 text-xs">
              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="w-4 h-4 text-lime-400 shrink-0 mt-0.5" />
                <div>
                  <strong className="text-white block font-extrabold">Official Verified Blue Checkmark</strong>
                  <span className="text-indigo-200/70">Displayed on your athlete profile, leaderboards, & court check-ins.</span>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="w-4 h-4 text-lime-400 shrink-0 mt-0.5" />
                <div>
                  <strong className="text-white block font-extrabold">Moderator Stat Audit</strong>
                  <span className="text-indigo-200/70">Platform moderators review game logs & video clips to verify true stats.</span>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="w-4 h-4 text-lime-400 shrink-0 mt-0.5" />
                <div>
                  <strong className="text-white block font-extrabold">Direct Scout Evaluations & Feedback</strong>
                  <span className="text-indigo-200/70">Receive official recruiting feedback reports from NCAA and FIBA scouts.</span>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="w-4 h-4 text-lime-400 shrink-0 mt-0.5" />
                <div>
                  <strong className="text-white block font-extrabold">Priority Recruiting Directory Listing</strong>
                  <span className="text-indigo-200/70">Your profile appears at the top of scout talent searches.</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={handleSubscribeVerifiedPro}
                className="w-full py-3.5 bg-gradient-to-r from-amber-400 to-lime-400 hover:from-amber-300 hover:to-lime-300 text-black font-black italic uppercase text-sm rounded-2xl shadow-xl transition flex items-center justify-center gap-2"
              >
                <Sparkles className="w-5 h-5 text-black" />
                <span>Subscribe for $20 / Month</span>
              </button>

              <button
                type="button"
                onClick={() => setShowUpgradeModal(false)}
                className="text-xs text-indigo-300 hover:text-white font-bold py-1"
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Certified Recruiter Pass Checkout Modal ($49/mo or $299/yr) */}
      {showRecruiterPassModal && (
        <div className="fixed inset-0 z-50 bg-indigo-950/85 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-gradient-to-b from-purple-950 via-indigo-950 to-slate-900 border border-purple-400/50 rounded-[2.5rem] p-6 sm:p-8 max-w-lg w-full text-white space-y-6 relative shadow-2xl">
            <button
              onClick={() => setShowRecruiterPassModal(false)}
              className="absolute top-5 right-5 text-indigo-300 hover:text-white font-bold text-lg"
            >
              ✕
            </button>

            <div className="text-center space-y-2">
              <div className="inline-flex items-center justify-center p-3 rounded-2xl bg-purple-500/20 text-purple-300 border border-purple-400/40">
                <Users className="w-8 h-8 text-purple-400" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-black italic uppercase tracking-tight text-white">
                Certified Recruiter Pass
              </h2>
              <p className="text-xs text-indigo-200/80 max-w-sm mx-auto">
                Unlock direct athlete contact sheets, raw 1080p game tapes, and official college & FIBA offer letter dispatching.
              </p>
            </div>

            {/* Billing Cycle Selector */}
            <div className="flex bg-indigo-950 p-1.5 rounded-2xl border border-white/10">
              <button
                type="button"
                onClick={() => setRecruiterPassBillingCycle('monthly')}
                className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition ${
                  recruiterPassBillingCycle === 'monthly'
                    ? 'bg-purple-600 text-white shadow-md'
                    : 'text-indigo-300 hover:text-white'
                }`}
              >
                Monthly ($49/mo)
              </button>

              <button
                type="button"
                onClick={() => setRecruiterPassBillingCycle('annual')}
                className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1 ${
                  recruiterPassBillingCycle === 'annual'
                    ? 'bg-purple-600 text-white shadow-md'
                    : 'text-indigo-300 hover:text-white'
                }`}
              >
                <span>Annual ($299/yr)</span>
                <span className="px-1.5 py-0.5 bg-lime-400 text-black font-black text-[9px] rounded-md uppercase">
                  Save 50%
                </span>
              </button>
            </div>

            {/* Privileges Checklist */}
            <div className="space-y-3 bg-indigo-900/60 p-4 rounded-2xl border border-white/10 text-xs">
              <div className="flex items-start gap-2.5">
                <Check className="w-4 h-4 text-purple-400 shrink-0 mt-0.5" />
                <div>
                  <strong className="text-white block font-extrabold">Unredacted Contact Sheets</strong>
                  <span className="text-indigo-200/70">View direct phone numbers & email addresses for top NCAA & FIBA prospects.</span>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <Check className="w-4 h-4 text-purple-400 shrink-0 mt-0.5" />
                <div>
                  <strong className="text-white block font-extrabold">Raw 1080p Game Tape Downloads</strong>
                  <span className="text-indigo-200/70">Export full uncompressed video reels and shot charts.</span>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <Check className="w-4 h-4 text-purple-400 shrink-0 mt-0.5" />
                <div>
                  <strong className="text-white block font-extrabold">AI Scouting Evaluation Generator</strong>
                  <span className="text-indigo-200/70">Generate instant scouting breakdowns and prospect metric comparisons.</span>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <Check className="w-4 h-4 text-purple-400 shrink-0 mt-0.5" />
                <div>
                  <strong className="text-white block font-extrabold">Official In-App Offer Dispatcher</strong>
                  <span className="text-indigo-200/70">Send official scholarship & recruitment letters directly to athlete inboxes.</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={handleActivateRecruiterPass}
                className="w-full py-3.5 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-400 hover:to-indigo-400 text-white font-black italic uppercase text-sm rounded-2xl shadow-xl transition flex items-center justify-center gap-2 border border-purple-300/30"
              >
                <CreditCard className="w-5 h-5 text-amber-300" />
                <span>
                  Confirm & Activate ({recruiterPassBillingCycle === 'monthly' ? '$49 / Month' : '$299 / Year'})
                </span>
              </button>

              <button
                type="button"
                onClick={() => setShowRecruiterPassModal(false)}
                className="text-xs text-indigo-300 hover:text-white font-bold py-1 text-center"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
